/**
 * The tree materialiser (PLAN §6.3): rows → `Playlist → Rundown → Segment →
 * Part → Piece` (+ adlibs), shared by the engine, the SPA, the Lambdas and the
 * tests.
 *
 * **Placement.** Every row is placed under exactly one key: its parent's row
 * id, `ROOT_KEY`, or `LOST_AND_FOUND_KEY` (a synthetic node). The rules, in
 * order:
 *   1. `parentId` null, or naming a row not in the set → the root, for a kind
 *      that may be a root (a rundown whose playlist is in another partition is
 *      a root of its own rows); otherwise lost & found (an orphan).
 *   2. A parent of a kind this kind may not hang under → lost & found.
 *   3. A cycle of parent edges → the member whose `parentId` was written LAST
 *      (greatest HLC in `h.parentId`) is cut to lost & found; every other
 *      member keeps its parent. "The older parentId write wins" — and because
 *      HLCs are unique and every replica sees the same rows, every replica
 *      cuts the same edge (PLAN §6.2: "breaks it deterministically").
 *   4. Otherwise → its parent.
 * With the kind rules of PLAN §5.1 a cycle cannot form (each level hangs under
 * a strictly higher one); rule 3 is what keeps the materialiser total if the
 * rules ever allow nesting, and the tests exercise it with permissive rules.
 *
 * **Visibility.** Siblings are sorted by `ord`, then row id (`compareByOrd`).
 * A soft-deleted row is left out of its parent's children unless
 * `includeDeleted` — which hides its whole subtree too (the descendants stay
 * placed under it, unreachable, so an undelete brings them back as they were).
 * A row moved under a deleted parent is therefore hidden, not lost.
 *
 * **Incremental.** `upsertRow` recomputes the placement only of the rows a
 * change can move — the row itself, rows naming it as parent when it first
 * appears, and the members of any cycle through it before or after — and
 * re-sorts only the sibling lists they leave and join (PLAN §6.3: "one changed
 * row re-sorts only its parent"). The property test holds it equal to
 * `buildTree` over every generated patch sequence.
 *
 * The tree is a mutable index (`upsertRow` updates it in place); every sibling
 * list it changes is replaced by a new array, so a consumer holding the old
 * list can tell by identity what changed. Pure otherwise: no I/O, no clock.
 */
import { ALLOWED_PARENT_KINDS, ROOT_KINDS, type RowKind } from './fields.js';
import { compareHlc } from './hlc.js';
import { compareByOrd } from './ordering.js';
import type { Row } from './row.js';

/** The key of the top level. */
export const ROOT_KEY = '#root';
/** The synthetic "Lost & found" node: orphans and cut cycle members. */
export const LOST_AND_FOUND_KEY = '#lost-and-found';

export type TreeOptions = {
  /** Show soft-deleted rows (and so their subtrees). */
  readonly includeDeleted: boolean;
  /** Which parent kinds each kind accepts. Production code passes `ALLOWED_PARENT_KINDS`. */
  readonly allowedParentKinds: Readonly<Record<RowKind, readonly RowKind[]>>;
  /** Kinds that may sit at the root. Production code passes `ROOT_KINDS`. */
  readonly rootKinds: readonly RowKind[];
};

/** The production options: PLAN §5.1's hierarchy, deleted rows hidden. */
export const STANDARD_TREE_OPTIONS: TreeOptions = {
  includeDeleted: false,
  allowedParentKinds: ALLOWED_PARENT_KINDS,
  rootKinds: ROOT_KINDS,
};

export type RowTree = {
  readonly options: TreeOptions;
  readonly rows: Map<string, Row>;
  /** Where each row is placed: a row id, `ROOT_KEY` or `LOST_AND_FOUND_KEY`. */
  readonly placement: Map<string, string>;
  /** Visible children per key, sorted. A key with no visible children is absent. */
  readonly children: Map<string, readonly string[]>;
  /** Row ids by their raw `parentId`, whatever their placement — the incremental index. */
  readonly rawChildren: Map<string, Set<string>>;
};

export type TreeNode = { readonly row: Row; readonly children: readonly TreeNode[] };

export function createTree(options: TreeOptions): RowTree {
  return { options, rows: new Map(), placement: new Map(), children: new Map(), rawChildren: new Map() };
}

function isVisible(tree: RowTree, row: Row): boolean {
  return tree.options.includeDeleted || !row.deleted;
}

/** The parent row `row` would hang under, if the edge is valid (exists, allowed kind). */
function validParent(tree: RowTree, row: Row): Row | null {
  if (row.parentId === null) return null;
  const parent = tree.rows.get(row.parentId);
  if (parent === undefined) return null;
  return tree.options.allowedParentKinds[row.kind].includes(parent.kind) ? parent : null;
}

/**
 * The members of the cycle of valid edges through `row`, starting at `row`,
 * or null when `row` is on no cycle (its chain ends, or runs into a cycle it
 * is not part of).
 */
function cycleThrough(tree: RowTree, row: Row): Row[] | null {
  const path: Row[] = [row];
  const seen = new Set<string>([row.rowId]);
  let current = validParent(tree, row);
  while (current !== null) {
    if (current.rowId === row.rowId) return path;
    if (seen.has(current.rowId)) return null;
    seen.add(current.rowId);
    path.push(current);
    current = validParent(tree, current);
  }
  return null;
}

function parentClock(row: Row): string | null {
  return Object.hasOwn(row.h, 'parentId') ? (row.h['parentId'] ?? null) : null;
}

/** Newer parentId write first; row id breaks a tie a unique-HLC world never produces. */
function laterParentWrite(a: Row, b: Row): Row {
  const ha = parentClock(a);
  const hb = parentClock(b);
  if (ha !== hb) {
    if (ha === null) return b;
    if (hb === null) return a;
    return compareHlc(ha, hb) > 0 ? a : b;
  }
  return a.rowId > b.rowId ? a : b;
}

/** Rule 1-4 of the module docstring. */
export function placementOf(tree: RowTree, row: Row): string {
  const rootCapable = tree.options.rootKinds.includes(row.kind);
  const allowed = tree.options.allowedParentKinds[row.kind];
  if (row.parentId === null) return rootCapable ? ROOT_KEY : LOST_AND_FOUND_KEY;
  if (allowed.length === 0) return LOST_AND_FOUND_KEY;
  const parent = tree.rows.get(row.parentId);
  if (parent === undefined) return rootCapable ? ROOT_KEY : LOST_AND_FOUND_KEY;
  if (!allowed.includes(parent.kind)) return LOST_AND_FOUND_KEY;
  const cycle = cycleThrough(tree, row);
  if (cycle !== null && cycle.reduce(laterParentWrite).rowId === row.rowId) return LOST_AND_FOUND_KEY;
  return row.parentId;
}

function compareIds(tree: RowTree, a: string, b: string): number {
  const ra = tree.rows.get(a);
  const rb = tree.rows.get(b);
  return compareByOrd({ id: a, ord: ra?.ord ?? '' }, { id: b, ord: rb?.ord ?? '' });
}

function indexRaw(tree: RowTree, row: Row): void {
  if (row.parentId === null) return;
  const set = tree.rawChildren.get(row.parentId) ?? new Set<string>();
  set.add(row.rowId);
  tree.rawChildren.set(row.parentId, set);
}

function unindexRaw(tree: RowTree, row: Row): void {
  if (row.parentId === null) return;
  const set = tree.rawChildren.get(row.parentId);
  if (set === undefined) return;
  set.delete(row.rowId);
  if (set.size === 0) tree.rawChildren.delete(row.parentId);
}

/** Full rebuild: place every row, then sort every sibling list. */
export function buildTree(rows: Iterable<Row>, options: TreeOptions): RowTree {
  const tree = createTree(options);
  for (const row of rows) {
    tree.rows.set(row.rowId, row);
    indexRaw(tree, row);
  }
  const lists = new Map<string, string[]>();
  for (const row of tree.rows.values()) {
    const key = placementOf(tree, row);
    tree.placement.set(row.rowId, key);
    if (!isVisible(tree, row)) continue;
    const list = lists.get(key) ?? [];
    list.push(row.rowId);
    lists.set(key, list);
  }
  for (const [key, list] of lists) tree.children.set(key, list.sort((a, b) => compareIds(tree, a, b)));
  return tree;
}

function removeChild(tree: RowTree, key: string, rowId: string): void {
  const list = tree.children.get(key);
  if (list === undefined || !list.includes(rowId)) return;
  const next = list.filter((id) => id !== rowId);
  if (next.length === 0) tree.children.delete(key);
  else tree.children.set(key, next);
}

function insertChild(tree: RowTree, key: string, rowId: string): void {
  const list = tree.children.get(key) ?? [];
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const other = list[mid];
    if (other !== undefined && compareIds(tree, other, rowId) < 0) lo = mid + 1;
    else hi = mid;
  }
  tree.children.set(key, [...list.slice(0, lo), rowId, ...list.slice(lo)]);
}

/** Members of the cycle `row` was on before its edge changed: walk from its old parent back to it. */
function formerCycle(tree: RowTree, before: Row): readonly string[] {
  const oldParent = validParent(tree, before);
  const members: string[] = [];
  const seen = new Set<string>();
  let current = oldParent;
  while (current !== null && !seen.has(current.rowId)) {
    if (current.rowId === before.rowId) return members;
    seen.add(current.rowId);
    members.push(current.rowId);
    current = validParent(tree, current);
  }
  return [];
}

function affectedBy(tree: RowTree, before: Row | null, row: Row): Set<string> {
  const affected = new Set<string>([row.rowId]);
  if (before === null || before.kind !== row.kind) {
    for (const child of tree.rawChildren.get(row.rowId) ?? []) affected.add(child);
  }
  for (const member of cycleThrough(tree, row) ?? []) affected.add(member.rowId);
  if (before !== null) for (const member of formerCycle(tree, before)) affected.add(member);
  return affected;
}

/**
 * Apply one changed (or new) row. Returns the keys whose sibling lists
 * changed — the parents a view must re-render.
 */
export function upsertRow(tree: RowTree, row: Row): readonly string[] {
  const before = tree.rows.get(row.rowId) ?? null;
  if (before !== null) unindexRaw(tree, before);
  tree.rows.set(row.rowId, row);
  indexRaw(tree, row);

  const changed = new Set<string>();
  for (const id of affectedBy(tree, before, row)) {
    const current = tree.rows.get(id);
    if (current === undefined) continue;
    for (const key of relocate(tree, id === row.rowId ? before : current, current)) changed.add(key);
  }
  return [...changed].sort();
}

/**
 * Re-place one row whose own state went from `previous` (null: new) to
 * `current`, moving it between sibling lists if its placement, visibility or
 * `ord` changed. Returns the keys whose lists changed.
 */
function relocate(tree: RowTree, previous: Row | null, current: Row): string[] {
  const oldKey = tree.placement.get(current.rowId) ?? null;
  const newKey = placementOf(tree, current);
  tree.placement.set(current.rowId, newKey);
  const wasVisible = previous !== null && isVisible(tree, previous);
  const nowVisible = isVisible(tree, current);
  const reordered = previous !== null && previous.ord !== current.ord;
  if (oldKey === newKey && wasVisible === nowVisible && !reordered) return [];
  const changed: string[] = [];
  if (oldKey !== null && wasVisible) {
    removeChild(tree, oldKey, current.rowId);
    changed.push(oldKey);
  }
  if (nowVisible) {
    insertChild(tree, newKey, current.rowId);
    changed.push(newKey);
  }
  return changed;
}

/** The visible child rows of a key (a row id, `ROOT_KEY` or `LOST_AND_FOUND_KEY`), sorted. */
export function childrenOf(tree: RowTree, key: string): readonly Row[] {
  const out: Row[] = [];
  for (const id of tree.children.get(key) ?? []) {
    const row = tree.rows.get(id);
    if (row !== undefined) out.push(row);
  }
  return out;
}

function nodesUnder(tree: RowTree, key: string, seen: Set<string>): TreeNode[] {
  const out: TreeNode[] = [];
  for (const row of childrenOf(tree, key)) {
    if (seen.has(row.rowId)) continue;
    seen.add(row.rowId);
    out.push({ row, children: nodesUnder(tree, row.rowId, seen) });
  }
  return out;
}

/** The visible tree as nested nodes: the roots, and the lost & found node's contents. */
export function nestedTree(tree: RowTree): { readonly roots: readonly TreeNode[]; readonly lostAndFound: readonly TreeNode[] } {
  const seen = new Set<string>();
  return { roots: nodesUnder(tree, ROOT_KEY, seen), lostAndFound: nodesUnder(tree, LOST_AND_FOUND_KEY, seen) };
}
